import React, { useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { resolveDashboardHubUrl } from '../utils/dashboardHubUrl';
import { resolveAgentGroupIdForDashboardHub } from '../utils/agentDashboardHub';
import { showInfo } from '../utils/notifications';
import activationPingSound from '../sounds/universfield-new-notification-022-370046.mp3';

/** أخطاء متوقعة عند إلغاء الاتصال أثناء التفاوض (مثلاً React Strict Mode يُعيد تشغيل التأثير) */
function isBenignHubConnectionError(e: unknown): boolean {
  if (e == null) return false;
  const err = e as Error & { message?: string };
  if (err.name === 'AbortError') return true;
  const msg = String(err.message || err).toLowerCase();
  return (
    msg.includes('stopped during negotiation') ||
    msg.includes('connection was stopped') ||
    msg.includes('before stop() was called') ||
    msg.includes('connection disconnected') ||
    msg.includes('connection is closing') ||
    msg.includes('connection is closed')
  );
}

/** حمولة activationPointRequestCreated من DashboardHub */
function pickRequest(payload: unknown): Record<string, unknown> | null {
  if (payload == null) return null;
  if (typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  const nested = p.request ?? p.Request;
  if (nested && typeof nested === 'object') return nested as Record<string, unknown>;
  return p;
}

function playSound(audioEl: HTMLAudioElement | null): void {
  if (!audioEl) return;
  audioEl.currentTime = 0;
  audioEl.play().catch(() => {
    /* قيود تشغيل الصوت قبل تفاعل المستخدم — يُتجاهل بهدوء */
  });
}

function tryDesktopNotification(title: string, body: string): void {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    new Notification(title, { body, lang: 'ar' });
  } catch {
    /* بعض المتصفحات أو السياقات ترفض الإنشاء */
  }
}

const STOP_DEFER_MS = 450;

/**
 * يستمع لحدث activationPointRequestCreated بعد JoinAgentGroup؛
 * toast + صوت + إشعار سطح المكتب إن وُجد الإذن.
 *
 * ملاحظة: في وضع التطوير مع React.StrictMode يُشغَّل التأثير مرتين؛ استدعاء stop()
 * أثناء start() يلغي التفاوض. نؤجل stop() قليلاً ونتجاهل أخطاء الإلغاء البنّاء.
 */
const ActivationPointRequestSignalRListener: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const agentGroupId = resolveAgentGroupIdForDashboardHub(user);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    audioRef.current = new Audio(activationPingSound);
    audioRef.current.preload = 'auto';
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!agentGroupId) return undefined;

    let effectActive = true;
    let stopTimer: ReturnType<typeof setTimeout> | null = null;
    const hubUrl = resolveDashboardHubUrl(apiService.getBaseURL());

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => localStorage.getItem('token') ?? '',
      })
      .withAutomaticReconnect()
      .build();

    const joinGroup = async () => {
      try {
        await connection.invoke('JoinAgentGroup', agentGroupId);
      } catch (e) {
        console.warn('[DashboardHub] JoinAgentGroup failed', e);
      }
    };

    const onCreated = (payload: unknown) => {
      const req = pickRequest(payload);
      const subscriberName = String(req?.subscriberName ?? req?.SubscriberName ?? '').trim() || '—';
      const activationPointName =
        String(req?.activationPointName ?? req?.ActivationPointName ?? '').trim() || '—';
      const amountRaw = req?.amountReceived ?? req?.AmountReceived;
      const amountLabel =
        amountRaw != null && Number.isFinite(Number(amountRaw))
          ? String(Number(amountRaw))
          : '—';

      showInfo(
        'طلب تفعيل جديد',
        `من نقطة: ${activationPointName} — ${subscriberName} — المبلغ: ${amountLabel}`,
        10000
      );
      playSound(audioRef.current);
      tryDesktopNotification('طلب تفعيل جديد', `${subscriberName} — ${amountLabel} د.ع — ${activationPointName}`);

      queryClient.invalidateQueries({ queryKey: ['activation-point-requests'] });
      queryClient.invalidateQueries({ queryKey: ['activation-points-options'] });
    };

    connection.on('activationPointRequestCreated', onCreated);

    connection.onreconnected(() => {
      void joinGroup();
    });

    void (async () => {
      try {
        await connection.start();
      } catch (e) {
        if (effectActive && !isBenignHubConnectionError(e)) {
          console.warn('[DashboardHub] connection start failed', e);
        }
        return;
      }

      if (!effectActive) {
        try {
          await connection.stop();
        } catch {
          /* ignore */
        }
        return;
      }

      await joinGroup();
    })();

    return () => {
      effectActive = false;
      connection.off('activationPointRequestCreated', onCreated);
      if (stopTimer) clearTimeout(stopTimer);
      stopTimer = setTimeout(() => {
        void connection.stop().catch(() => {
          /* إغلاق بعد تأخير — تجاهل */
        });
      }, STOP_DEFER_MS);
    };
  }, [agentGroupId, queryClient]);

  return null;
};

export default ActivationPointRequestSignalRListener;
